import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Building2,
  Loader2,
  FileText,
} from "lucide-react";
import { formatMinute } from "@convex/lib/time";
import StatusIcon from "@/components/StatusIcon";
import { statusLabel } from "@/lib/status";
import {
  WEEKDAYS,
  buildDays,
  dateKeyOf,
  lessonTimeLabel,
  pad,
  type CalendarDay,
  type CalendarLesson,
  type CalendarView,
} from "@/lib/calendar";

export interface LiquidCalendarProps {
  /** Lessons keyed by local date, "YYYY-MM-DD". */
  lessonsByDate: Record<string, CalendarLesson[]>;
  /** Controlled: the parent owns the cursor so it can query the right range. */
  cursor: Date;
  onCursorChange: (date: Date) => void;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  /** True while the range query is in flight. */
  loading?: boolean;
  /** Rendered inside the lesson dialog, above the Close button. */
  renderLessonExtra?: (lesson: CalendarLesson, date: Date) => ReactNode;
  onSelectLesson?: (lesson: CalendarLesson, date: Date) => void;
  className?: string;
}

export default function LiquidCalendar({
  lessonsByDate,
  cursor,
  onCursorChange,
  view,
  onViewChange,
  loading = false,
  renderLessonExtra,
  onSelectLesson,
  className = "",
}: LiquidCalendarProps) {
  const [selected, setSelected] = useState<{
    lesson: CalendarLesson;
    date: Date;
  } | null>(null);

  const days = useMemo(() => buildDays(cursor, view), [cursor, view]);

  const shift = (direction: -1 | 1) => {
    const next = new Date(cursor);
    if (view === "week") {
      next.setDate(next.getDate() + 7 * direction);
    } else {
      // Snap to the 1st first: month arithmetic on the 31st skips a month.
      next.setDate(1);
      next.setMonth(next.getMonth() + direction);
    }
    onCursorChange(next);
  };

  const rangeText = useMemo(() => {
    if (view === "month") {
      return cursor
        .toLocaleDateString("en-GB", { month: "long", year: "numeric" })
        .toUpperCase();
    }
    if (days.length < 7) return "";
    return `${days[0].formattedDate} - ${days[6].formattedDate}.${days[6].date.getFullYear()}`;
  }, [cursor, days, view]);

  // The dialog stays open across a reactive update, so re-read the live lesson.
  const selectedLive = useMemo(() => {
    if (selected === null) return null;
    const list = lessonsByDate[dateKeyOf(selected.date)] ?? [];
    return list.find((lesson) => lesson.id === selected.lesson.id) ?? selected.lesson;
  }, [selected, lessonsByDate]);

  const openLesson = (lesson: CalendarLesson, date: Date) => {
    setSelected({ lesson, date });
    onSelectLesson?.(lesson, date);
  };

  return (
    <div
      className={`liquid-calendar-container min-h-full bg-[#fdfdfc] text-[#222] p-3 sm:p-6 md:p-8 font-sans dark:bg-[#0f0f0e] dark:text-[#ededeb] transition-colors ${className}`}
    >
      <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-200 dark:border-neutral-800 pb-3 mb-4">
        <div className="flex items-center justify-between sm:justify-start gap-2 pb-1 sm:pb-2 sm:-mb-3.5 sm:border-b-2 sm:border-red-600 text-red-600 dark:text-red-500 font-medium text-sm">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            <span>Calendar</span>
            {loading && <Loader2 className="h-3 w-3 animate-spin opacity-70" />}
          </div>

          {/* Mobile view toggle */}
          <div className="relative flex sm:hidden items-center rounded-md border border-neutral-200 dark:border-neutral-800 p-0.5">
            {(["week", "month"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => onViewChange(option)}
                aria-pressed={view === option}
                className={[
                  "relative cursor-pointer rounded px-2.5 py-0.5 font-mono text-[11px] capitalize transition-colors duration-150",
                  view === option
                    ? "font-semibold text-neutral-900 dark:text-neutral-100"
                    : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100",
                ].join(" ")}
              >
                {view === option && (
                  <motion.span
                    layoutId="calendar-view-pill-mobile"
                    className="absolute inset-0 rounded bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                    transition={{ type: "spring", stiffness: 450, damping: 35 }}
                  />
                )}
                <span className="relative z-10">{option}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:items-end gap-1 sm:gap-0.5">
          <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
            {/* Desktop view toggle */}
            <div className="hidden sm:flex relative items-center rounded-md border border-neutral-200 dark:border-neutral-800 p-0.5">
              {(["week", "month"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onViewChange(option)}
                  aria-pressed={view === option}
                  className={[
                    "relative cursor-pointer rounded px-2.5 py-0.5 font-mono text-[11px] capitalize transition-colors duration-150",
                    view === option
                      ? "font-semibold text-neutral-900 dark:text-neutral-100"
                      : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100",
                  ].join(" ")}
                >
                  {view === option && (
                    <motion.span
                      layoutId="calendar-view-pill-desktop"
                      className="absolute inset-0 rounded bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                      transition={{ type: "spring", stiffness: 450, damping: 35 }}
                    />
                  )}
                  <span className="relative z-10">{option}</span>
                </button>
              ))}
            </div>
            <span className="text-xs sm:text-sm font-semibold text-neutral-800 dark:text-neutral-200 font-mono tracking-tight">
              {rangeText}
            </span>
            <div className="flex items-center gap-1">
              <motion.button
                type="button"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => shift(-1)}
                aria-label="Previous"
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 transition-colors cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => shift(1)}
                aria-label="Next"
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 transition-colors cursor-pointer"
              >
                <ChevronRight className="h-4 w-4" />
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onCursorChange(new Date())}
                className="ml-1 text-xs font-semibold px-2.5 sm:px-3 py-1 rounded bg-[#e53e3e] hover:bg-[#c53030] text-white shadow-xs transition-colors cursor-pointer"
              >
                Today
              </motion.button>
            </div>
          </div>
          <p className="hidden sm:block text-[11px] text-neutral-400 dark:text-neutral-500 font-mono">
            Lessons materialized from the weekly schedule
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${view}-${rangeText}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
        >
          {view === "week" ? (
            <WeekGrid days={days} lessonsByDate={lessonsByDate} onOpen={openLesson} />
          ) : (
            <MonthGrid days={days} lessonsByDate={lessonsByDate} onOpen={openLesson} />
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {selected !== null && selectedLive !== null && (
          <LessonDialog
            lesson={selectedLive}
            date={selected.date}
            cursorDate={cursor}
            view={view}
            extra={renderLessonExtra?.(selectedLive, selected.date)}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------- week view

function WeekGrid({
  days,
  lessonsByDate,
  onOpen,
}: {
  days: CalendarDay[];
  lessonsByDate: Record<string, CalendarLesson[]>;
  onOpen: (lesson: CalendarLesson, date: Date) => void;
}) {
  const todayDay = days.find((d) => d.isToday);
  const [activeDayKey, setActiveDayKey] = useState<string>(
    todayDay ? todayDay.dateKey : days[0]?.dateKey ?? ""
  );

  const currentActiveKey = days.some((d) => d.dateKey === activeDayKey)
    ? activeDayKey
    : (todayDay ? todayDay.dateKey : days[0]?.dateKey ?? "");

  const activeDay = days.find((d) => d.dateKey === currentActiveKey) ?? days[0];
  const activeLessons = lessonsByDate[currentActiveKey] ?? [];

  return (
    <div>
      {/* Mobile Day Strip & Agenda Cards */}
      <div className="md:hidden flex flex-col gap-3">
        <div className="grid grid-cols-7 gap-1 border border-neutral-200 dark:border-neutral-800 bg-[#fbfbfa] dark:bg-[#171716] p-1 rounded-xl shadow-2xs">
          {days.map((day) => {
            const lessons = lessonsByDate[day.dateKey] ?? [];
            const isSelected = day.dateKey === currentActiveKey;
            return (
              <button
                key={`mob-wh-${day.dateKey}`}
                type="button"
                onClick={() => setActiveDayKey(day.dateKey)}
                className={[
                  "relative flex flex-col items-center py-2 px-0.5 rounded-lg transition-all cursor-pointer",
                  isSelected
                    ? "bg-white dark:bg-[#242422] shadow-xs"
                    : "hover:bg-neutral-100 dark:hover:bg-neutral-800/60",
                ].join(" ")}
              >
                <span className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase">
                  {day.weekdayShort}
                </span>
                <span
                  className={[
                    "text-xs font-bold mt-0.5",
                    day.isToday
                      ? "text-red-600 dark:text-red-500"
                      : "text-neutral-800 dark:text-neutral-200",
                  ].join(" ")}
                >
                  {day.dayNumber}
                </span>
                {lessons.length > 0 && (
                  <div className="flex items-center gap-0.5 mt-1">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: lessons[0].color ?? "#EF4444" }}
                    />
                    {lessons.length > 1 && (
                      <span className="text-[8px] font-mono leading-none text-neutral-400">
                        {lessons.length}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Active Day Lessons */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between px-1">
            <span className="font-mono text-xs font-semibold text-neutral-800 dark:text-neutral-200">
              {activeDay?.weekdayFull} · {activeDay?.formattedDate}
            </span>
            <span className="font-mono text-[11px] text-neutral-400">
              {activeLessons.length} {activeLessons.length === 1 ? "lesson" : "lessons"}
            </span>
          </div>

          {activeLessons.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-800 py-10 text-center text-xs font-mono text-neutral-400 dark:text-neutral-500 bg-[#fbfbfa]/50 dark:bg-[#171716]/30">
              No lessons scheduled for this day
            </div>
          ) : (
            activeLessons.map((lesson) => (
              <LessonCard
                key={lesson.id}
                lesson={lesson}
                onClick={() => onOpen(lesson, activeDay.date)}
              />
            ))
          )}
        </div>
      </div>

      {/* Desktop Week Grid */}
      <div className="hidden md:block overflow-x-auto">
        <div className="min-w-[850px] border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#121212] rounded-md shadow-xs">
          <div className="grid grid-cols-7 border-b border-neutral-200 dark:border-neutral-800 bg-[#fbfbfa] dark:bg-[#171716]">
            {days.map((day) => (
              <div
                key={`wh-${day.dateKey}`}
                className="py-3 px-2 text-center border-r last:border-r-0 border-neutral-200 dark:border-neutral-800"
              >
                <div className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                  {day.weekdayFull}
                </div>
                <div
                  className={[
                    "text-sm font-bold mt-0.5",
                    day.isToday
                      ? "text-red-600 dark:text-red-500"
                      : "text-neutral-800 dark:text-neutral-200",
                  ].join(" ")}
                >
                  {day.formattedDate}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 min-h-[620px]">
            {days.map((day) => {
              const lessons = lessonsByDate[day.dateKey] ?? [];
              return (
                <div
                  key={`wc-${day.dateKey}`}
                  className="p-2.5 flex flex-col gap-2.5 border-r last:border-r-0 border-neutral-200/80 dark:border-neutral-800/80 bg-neutral-50/20 dark:bg-transparent"
                >
                  {lessons.map((lesson) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson}
                      onClick={() => onOpen(lesson, day.date)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function LessonCard({
  lesson,
  onClick,
}: {
  lesson: CalendarLesson;
  onClick: () => void;
}) {
  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}
      whileTap={{ scale: 0.99 }}
      transition={{ duration: 0.12, ease: "easeOut" }}
      onClick={onClick}
      style={{
        borderLeftColor: lesson.color ?? "#9CA3AF",
        borderLeftWidth: "4px",
      }}
      className={[
        "rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#18181b] p-3 text-left shadow-2xs hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors cursor-pointer relative",
        lesson.cancelled ? "opacity-50" : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5">
          {lesson.shortCode && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono"
              style={{
                backgroundColor: `${lesson.color ?? "#9CA3AF"}1A`,
                color: lesson.color ?? "#9CA3AF",
              }}
            >
              {lesson.shortCode}
            </span>
          )}
          <span
            className={[
              "font-bold text-xs text-neutral-900 dark:text-neutral-100 font-mono",
              lesson.cancelled ? "line-through" : "",
            ].join(" ")}
          >
            {lessonTimeLabel(lesson)}
          </span>
        </div>
        {lesson.category && (
          <span className="text-[10px] text-neutral-400 dark:text-neutral-500">
            {lesson.category}
          </span>
        )}
      </div>

      <div className="font-medium text-xs text-neutral-800 dark:text-neutral-200 mt-1.5 leading-snug">
        {lesson.title}
      </div>

      <div className="flex items-center justify-between mt-2.5 pt-1.5 border-t border-neutral-100 dark:border-neutral-800/60">
        {lesson.room ? (
          <div className="text-[11px] text-neutral-500 dark:text-neutral-400 flex items-center gap-1 font-mono">
            <Building2 className="h-3 w-3 shrink-0 text-neutral-400" />
            <span>{lesson.room}</span>
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-1.5">
          {lesson.cancelled && (
            <span className="font-mono text-[10px] uppercase tracking-wide text-neutral-400">
              Cancelled
            </span>
          )}
          <StatusIcon
            status={lesson.status}
            partialAudio={lesson.partialAudio}
            needsReview={lesson.needsReview}
          />
        </div>
      </div>
    </motion.div>
  );
}

// --------------------------------------------------------------- month view

function MonthGrid({
  days,
  lessonsByDate,
  onOpen,
}: {
  days: CalendarDay[];
  lessonsByDate: Record<string, CalendarLesson[]>;
  onOpen: (lesson: CalendarLesson, date: Date) => void;
}) {
  const todayDay = days.find((d) => d.isToday);
  const [selectedMonthDayKey, setSelectedMonthDayKey] = useState<string>(
    todayDay ? todayDay.dateKey : days.find((d) => d.inCursorMonth)?.dateKey ?? days[0]?.dateKey ?? ""
  );
  const currentMonthKey = days.some((d) => d.dateKey === selectedMonthDayKey)
    ? selectedMonthDayKey
    : (todayDay ? todayDay.dateKey : days.find((d) => d.inCursorMonth)?.dateKey ?? days[0]?.dateKey ?? "");

  const selectedMonthDay = days.find((d) => d.dateKey === currentMonthKey);
  const selectedMonthLessons = selectedMonthDay ? (lessonsByDate[selectedMonthDay.dateKey] ?? []) : [];

  return (
    <div>
      {/* Mobile Month View */}
      <div className="md:hidden flex flex-col gap-3">
        <div className="border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#121212] rounded-xl overflow-hidden shadow-xs">
          <div className="grid grid-cols-7 border-b border-neutral-200 dark:border-neutral-800 bg-[#fbfbfa] dark:bg-[#171716] text-center py-2 text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">
            {WEEKDAYS.map((weekday) => (
              <div key={`mob-mh-${weekday.short}`}>{weekday.short}</div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((day) => {
              const lessons = lessonsByDate[day.dateKey] ?? [];
              const isSelected = day.dateKey === currentMonthKey;
              return (
                <button
                  key={`mob-mc-${day.dateKey}`}
                  type="button"
                  onClick={() => setSelectedMonthDayKey(day.dateKey)}
                  className={[
                    "min-h-[50px] p-1 flex flex-col items-center justify-between border-b border-r last:border-r-0 border-neutral-100 dark:border-neutral-800/80 transition-colors cursor-pointer",
                    day.inCursorMonth ? "" : "opacity-35 bg-neutral-50/50 dark:bg-white/[0.01]",
                    isSelected ? "bg-neutral-100 dark:bg-neutral-800 font-bold" : "hover:bg-neutral-50 dark:hover:bg-neutral-900/40",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "text-xs font-mono",
                      day.isToday ? "text-red-600 dark:text-red-500 font-bold" : "",
                    ].join(" ")}
                  >
                    {day.dayNumber}
                  </span>
                  <div className="flex items-center gap-0.5 mt-0.5 min-h-[6px]">
                    {lessons.slice(0, 3).map((l, i) => (
                      <span
                        key={i}
                        className="h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: l.color ?? "#9CA3AF" }}
                      />
                    ))}
                    {lessons.length > 3 && (
                      <span className="text-[8px] font-mono leading-none text-neutral-400">+</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day's lessons under the calendar */}
        {selectedMonthDay && (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center justify-between px-1">
              <span className="font-mono text-xs font-semibold text-neutral-800 dark:text-neutral-200">
                {selectedMonthDay.formattedDate}.{selectedMonthDay.date.getFullYear()} ({selectedMonthDay.weekdayFull})
              </span>
              <span className="font-mono text-[11px] text-neutral-400">
                {selectedMonthLessons.length} {selectedMonthLessons.length === 1 ? "lesson" : "lessons"}
              </span>
            </div>
            {selectedMonthLessons.length === 0 ? (
              <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-800 py-6 text-center text-xs font-mono text-neutral-400 dark:text-neutral-500 bg-[#fbfbfa]/50 dark:bg-[#171716]/30">
                No lessons on this date
              </div>
            ) : (
              selectedMonthLessons.map((lesson) => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  onClick={() => onOpen(lesson, selectedMonthDay.date)}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Desktop Month Grid */}
      <div className="hidden md:block overflow-x-auto">
        <div className="min-w-[720px] border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#121212] rounded-md shadow-xs">
          <div className="grid grid-cols-7 border-b border-neutral-200 dark:border-neutral-800 bg-[#fbfbfa] dark:bg-[#171716]">
            {WEEKDAYS.map((weekday) => (
              <div
                key={`mh-${weekday.short}`}
                className="py-2 px-2 text-center border-r last:border-r-0 border-neutral-200 dark:border-neutral-800 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500"
              >
                {weekday.short}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((day) => {
              const lessons = lessonsByDate[day.dateKey] ?? [];
              return (
                <div
                  key={`mc-${day.dateKey}`}
                  className={[
                    "min-h-[112px] p-1.5 flex flex-col gap-1 border-r border-b last:border-r-0 border-neutral-200/80 dark:border-neutral-800/80",
                    day.inCursorMonth
                      ? "bg-neutral-50/20 dark:bg-transparent"
                      : "bg-neutral-100/40 dark:bg-white/[0.02]",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "font-mono text-[11px] leading-none px-0.5",
                      day.isToday
                        ? "font-bold text-red-600 dark:text-red-500"
                        : day.inCursorMonth
                          ? "text-neutral-500 dark:text-neutral-400"
                          : "text-neutral-300 dark:text-neutral-600",
                    ].join(" ")}
                  >
                    {pad(day.dayNumber)}
                  </div>

                  {lessons.map((lesson) => (
                    <motion.button
                      key={lesson.id}
                      type="button"
                      whileHover={{ scale: 1.02, x: 1 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.12, ease: "easeOut" }}
                      onClick={() => onOpen(lesson, day.date)}
                      style={{ borderLeftColor: lesson.color ?? "#9CA3AF" }}
                      className={[
                        "flex w-full items-center gap-1 rounded-sm border-l-[3px] bg-neutral-50 px-1.5 py-1 text-left transition-colors hover:bg-neutral-100 dark:bg-white/[0.04] dark:hover:bg-white/[0.08] cursor-pointer",
                        lesson.cancelled ? "opacity-50" : "",
                      ].join(" ")}
                    >
                      <span className="font-mono text-[10px] text-neutral-500 dark:text-neutral-400 shrink-0">
                        {formatMinute(lesson.startMinute)}
                      </span>
                      <span className="truncate text-[10px] font-medium text-neutral-800 dark:text-neutral-200">
                        {lesson.shortCode ?? lesson.title}
                      </span>
                      <span className="ml-auto shrink-0">
                        <StatusIcon
                          status={lesson.status}
                          partialAudio={lesson.partialAudio}
                          needsReview={lesson.needsReview}
                        />
                      </span>
                    </motion.button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- dialog

function LessonDialog({
  lesson,
  date,
  cursorDate,
  view,
  extra,
  onClose,
}: {
  lesson: CalendarLesson;
  date: Date;
  cursorDate?: Date;
  view?: CalendarView;
  extra?: ReactNode;
  onClose: () => void;
}) {
  const rows: Array<[string, ReactNode]> = [
    ["Time", <span className="font-mono">{lessonTimeLabel(lesson)}</span>],
  ];
  if (lesson.room) rows.push(["Room", lesson.room]);
  if (lesson.category) rows.push(["Type", lesson.category]);
  rows.push([
    "Recording status",
    <span className="flex items-center gap-1.5">
      <StatusIcon
        status={lesson.status}
        partialAudio={lesson.partialAudio}
        needsReview={lesson.needsReview}
      />
      <span className="font-mono text-[11px]">
        {statusLabel(lesson.status)}
        {lesson.needsReview === true && " · check this match"}
      </span>
    </span>,
  ]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl border border-[#d1d1cf] bg-[#fdfdfc] p-4 sm:p-6 shadow-xl dark:border-[#2a2a28] dark:bg-[#141413] relative"
      >
        <div className="meniscus absolute top-0 left-0 w-full h-[1px] opacity-60" />
        <div className="flex items-center justify-between pb-3 border-b border-[#d1d1cf]/60 dark:border-[#2a2a28]">
          <span className="font-mono text-xs text-neutral-500 uppercase tracking-wider">
            Lesson Details
          </span>
          <span className="font-mono text-xs text-neutral-400">
            {date.toLocaleDateString("en-GB", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            {lesson.color && (
              <span
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: lesson.color }}
              />
            )}
            <h3
              className={[
                "text-lg font-semibold tracking-tight text-[#222] dark:text-[#ededeb]",
                lesson.cancelled ? "line-through opacity-60" : "",
              ].join(" ")}
            >
              {lesson.title}
            </h3>
          </div>

          {lesson.cancelled && (
            <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-neutral-400">
              Cancelled
            </p>
          )}

          <dl className="mt-4 space-y-2 text-xs">
            {rows.map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between py-1.5 border-b border-[#d1d1cf]/40 dark:border-[#2a2a28]/60"
              >
                <dt className="text-neutral-500 font-mono">{label}</dt>
                <dd className="font-medium text-[#222] dark:text-[#ededeb]">{value}</dd>
              </div>
            ))}
          </dl>

          {lesson.notes && (
            <p className="mt-3 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400">
              {lesson.notes}
            </p>
          )}
        </div>

        {extra !== undefined && extra !== null && <div className="mt-5">{extra}</div>}

        <div className="mt-6 flex items-center justify-between gap-3">
          {/* The dialog is a summary of the slot; the notes, transcript and
              audio live on their own page. */}
          <Link
            to={`/lesson/${lesson.id}`}
            state={{
              fromDate: cursorDate ? dateKeyOf(cursorDate) : dateKeyOf(date),
              fromView: view,
            }}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <FileText className="h-3.5 w-3.5" />
            Open lesson
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-xl bg-[#222] px-4 py-2 text-xs font-semibold tracking-wide text-[#fdfdfc] shadow-xs transition-all hover:bg-[#111] dark:bg-[#ededeb] dark:text-[#141413] dark:hover:bg-white"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
